"use client";

import { useState, useEffect, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/hooks/useAuth";
import { useSession } from "next-auth/react";
import {
  getUserProfile,
  uploadAvatar,
  updateUserProfile,
  deleteAvatar,
} from "@/lib/profile/api";
import { getMyItems } from "@/lib/api/profile";
import { UserProfile } from "@/data/profile/types";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileStats } from "@/components/profile/ProfileStats";
import { ProfileAbout } from "@/components/profile/ProfileAbout";
import { ItemCard } from "@/components/items/ItemCard";
import { BlockedUsersModal } from "@/components/profile/BlockedUsersModal";
import ShippingAddressModal from "@/components/profile/ShippingAddressModal";
import { EmailInputModal } from "@/components/auth/EmailInputModal";
import { PhoneInputModal } from "@/components/auth/PhoneInputModal";
import { EditNicknameModal } from "@/components/profile/EditNicknameModal";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Loader2,
  Package,
  Edit2,
  Shield,
  Lock,
  MapPin,
  Smartphone,
  Mail,
  Settings,
  ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { KOREAN_REGIONS_DETAILED, parseRegion, formatRegion } from "@/lib/utils/regions";

function MyPageContent() {
  const {
    user: currentUser,
    isLoading: authLoading,
    refreshUser,
    logout,
  } = useAuth();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  const isAuthenticated = currentUser || session?.user;
  const isLoading = authLoading || sessionStatus === "loading";
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [myItems, setMyItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [showNicknameEdit, setShowNicknameEdit] = useState(false);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [showShippingAddress, setShowShippingAddress] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showRegionEdit, setShowRegionEdit] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // 탭 상태 - URL 파라미터에서 가져오되, 없으면 프로필 탭으로 시작
  const tabParam = searchParams?.get("tab") || null;
  const [activeTab, setActiveTab] = useState<
    "profile" | "sales" | "account" | "shipping" | "blocked" | "support"
  >(() => {
    if (tabParam === "sales") return "sales";
    if (tabParam === "account") return "account";
    if (tabParam === "shipping") return "shipping";
    if (tabParam === "blocked") return "blocked";
    if (tabParam === "support") return "support";
    return "profile";
  });
  
  // URL 파라미터 변경 시 탭 상태 업데이트
  useEffect(() => {
    if (tabParam === "sales") setActiveTab("sales");
    else if (tabParam === "account") setActiveTab("account");
    else if (tabParam === "shipping") setActiveTab("shipping");
    else if (tabParam === "blocked") setActiveTab("blocked");
    else if (tabParam === "support") setActiveTab("support");
    else if (tabParam === null) setActiveTab("profile");
  }, [tabParam]);
  
  // 판매내역 탭 상태
  const [salesTab, setSalesTab] = useState<"selling" | "trading" | "sold">("selling");

  // 데이터 로드 함수들을 먼저 정의 (useEffect보다 위에)
  const loadProfile = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const result = await getUserProfile(currentUser.uid);

      if (result.success && result.data) {
        setProfile(result.data);
      }
    } catch (err) {
      console.error("프로필 로드 실패:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadMyItems = useCallback(async () => {
    if (!currentUser) return;

    try {
      setItemsLoading(true);
      const [sellingResult, soldResult] = await Promise.all([
        getMyItems({ userId: currentUser.uid, type: "selling" }),
        getMyItems({ userId: currentUser.uid, type: "sold" }),
      ]);
      
      const allItems = [
        ...(sellingResult.success && sellingResult.items ? sellingResult.items : []),
        ...(soldResult.success && soldResult.items ? soldResult.items : []),
      ];
      
      setMyItems(allItems);
    } catch (error) {
      console.error("내 상품 로딩 중 오류:", error);
    } finally {
      setItemsLoading(false);
    }
  }, [currentUser]);

  // 초기 인증 체크 완료 대기 - 더 안전한 방식
  useEffect(() => {
    // 인증 로딩이 완료될 때까지 기다림
    if (isLoading || sessionStatus === "loading") {
      return;
    }

    // 초기 체크 완료 플래그 설정 (더 긴 딜레이로 세션이 완전히 로드되도록)
    const timer = setTimeout(() => {
      setInitialCheckDone(true);
    }, 800); // 800ms로 증가

    return () => clearTimeout(timer);
  }, [isLoading, sessionStatus]);

  // 인증 체크 및 데이터 로드
  useEffect(() => {
    // 초기 체크가 완료되지 않았으면 대기
    if (!initialCheckDone) {
      return;
    }

    // 초기 체크가 완료되고, 로딩 중이 아니고, 사용자가 없을 때만 리다이렉트
    // 단, 이미 로그인 페이지에 있으면 리다이렉트하지 않음
    if (
      !isLoading &&
      sessionStatus !== "loading" &&
      !isAuthenticated &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/auth/")
    ) {
      router.push("/auth/login?next=/mypage");
      return;
    }

    // 인증된 사용자가 있으면 데이터 로드 (한 번만 실행)
    if (isAuthenticated && currentUser && !profile) {
      loadProfile();
      loadMyItems();
    }
  }, [initialCheckDone, isAuthenticated, isLoading, sessionStatus, currentUser, router, profile, loadProfile, loadMyItems]);

  const handleNicknameUpdate = async (newNickname: string) => {
    if (!currentUser) {
      toast.error("로그인이 필요합니다.");
      return;
    }

    try {
      const result = await updateUserProfile(currentUser.uid, {
        nickname: newNickname,
      });

      if (result.success) {
        setProfile(prev => (prev ? { ...prev, nickname: newNickname } : null));
        await refreshUser();
        window.location.reload();
      } else {
        toast.error(result.error || "닉네임 변경에 실패했습니다.");
      }
    } catch (error) {
      console.error("닉네임 변경 실패:", error);
      toast.error("닉네임 변경 중 오류가 발생했습니다.");
    }
  };

  const handleAvatarUpload = async (photoURL: string) => {
    if (!currentUser) return;

    try {
      if (photoURL) {
        const updateResult = await updateUserProfile(currentUser.uid, {
          photoURL: photoURL,
        });

        if (updateResult.success && profile) {
          setProfile({ ...profile, photoURL: photoURL });
          await refreshUser();
        } else {
          toast.error(updateResult.error || "아바타 업데이트에 실패했습니다.");
        }
      } else {
        const deleteResult = await deleteAvatar(
          currentUser.uid,
          profile?.photoURL
        );

        if (deleteResult.success && profile) {
          setProfile({ ...profile, photoURL: undefined });
          await refreshUser();
        } else {
          toast.error(deleteResult.error || "프로필 사진 삭제에 실패했습니다.");
        }
      }
    } catch (error) {
      console.error("아바타 처리 실패:", error);
      toast.error("아바타 처리 중 오류가 발생했습니다.");
    }
  };

  const handleProfileUpdate = (updatedProfile: UserProfile) => {
    setProfile(updatedProfile);
  };

  const handleEdit = () => {
    router.push("/profile/edit");
  };

  // 비밀번호 변경 함수
  const handlePasswordChange = async () => {
    if (!currentUser) {
      toast.error("로그인이 필요합니다.");
      return;
    }

    if (!currentPassword.trim()) {
      toast.error("현재 비밀번호를 입력해주세요.");
      return;
    }

    if (!newPassword.trim()) {
      toast.error("새 비밀번호를 입력해주세요.");
      return;
    }

    if (newPassword.length < 10) {
      toast.error("새 비밀번호는 10자 이상이어야 합니다.");
      return;
    }

    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasLowerCase || !hasNumbers || !hasSpecialChar) {
      toast.error("새 비밀번호는 소문자, 숫자, 특수문자를 포함해야 합니다.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("새 비밀번호가 일치하지 않습니다.");
      return;
    }

    if (currentPassword === newPassword) {
      toast.error("현재 비밀번호와 새 비밀번호가 같습니다.");
      return;
    }

    setChangingPassword(true);

    try {
      const { getAuth } = await import("@/lib/api/firebase-ultra-safe");
      const auth = await getAuth();
      const firebaseUser = auth.currentUser;

      if (!firebaseUser || !firebaseUser.email) {
        toast.error("사용자 정보를 찾을 수 없습니다.");
        return;
      }

      const credential = EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePassword(firebaseUser, newPassword);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordChange(false);
      toast.success("비밀번호가 변경되었습니다.");
    } catch (error: any) {
      console.error("비밀번호 변경 실패:", error);

      if (error.code === "auth/wrong-password") {
        toast.error("현재 비밀번호가 올바르지 않습니다.");
      } else if (error.code === "auth/weak-password") {
        toast.error("새 비밀번호가 너무 약합니다.");
      } else if (error.code === "auth/requires-recent-login") {
        toast.error("보안을 위해 다시 로그인해주세요.");
      } else {
        toast.error(`비밀번호 변경 실패: ${error.message || "알 수 없는 오류"}`);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // 거래지역 변경 함수
  const handleRegionChange = async () => {
    if (!currentUser) {
      toast.error("로그인이 필요합니다.");
      return;
    }

    const regionValue = selectedCity 
      ? formatRegion(selectedProvince, selectedCity)
      : selectedProvince;

    try {
      const result = await updateUserProfile(currentUser.uid, {
        region: regionValue.trim() || null,
      });

      if (result.success) {
        setProfile(prev =>
          prev ? { ...prev, region: regionValue.trim() || null } : null
        );
        setShowRegionEdit(false);
        setSelectedProvince("");
        setSelectedCity("");
      } else {
        toast.error(result.error || "거래지역 변경에 실패했습니다.");
      }
    } catch (error) {
      console.error("거래지역 변경 실패:", error);
      toast.error("거래지역 변경 중 오류가 발생했습니다.");
    }
  };

  // 인증 로딩 중이거나 초기 체크가 완료되지 않았으면 로딩 표시
  if (isLoading || !initialCheckDone || sessionStatus === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">프로필을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 초기 체크 완료 후 사용자가 없으면 아무것도 렌더링하지 않음 (리다이렉트 중)
  if (!currentUser && !session?.user) {
    return null;
  }

  // 프로필이 없을 때 기본 프로필 생성
  const displayProfile: UserProfile | null = profile || (currentUser ? {
    uid: currentUser.uid,
    email: currentUser.email || "",
    nickname: currentUser.nickname || currentUser.email?.split("@")[0] || "사용자",
    region: currentUser.region || "",
    grade: currentUser.grade || "C",
    photoURL: currentUser.profileImage || undefined,
    phoneNumber: currentUser.phoneNumber || "",
    phoneVerified: currentUser.isPhoneVerified || false,
    emailVerified: false, // 기본값
    tradesCount: currentUser.tradeCount || 0,
    reviewsCount: currentUser.reviewCount || 0,
    responseRate: 0, // 기본값
    createdAt: currentUser.createdAt ? (currentUser.createdAt instanceof Date ? currentUser.createdAt : new Date(currentUser.createdAt)) : new Date(),
    updatedAt: currentUser.updatedAt ? (currentUser.updatedAt instanceof Date ? currentUser.updatedAt : new Date(currentUser.updatedAt)) : new Date(),
  } : null);

  if (!displayProfile) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfileHeader user={displayProfile} isOwnProfile={true} onEdit={handleEdit} />

      {/* 탭 네비게이션 */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
          <nav className="flex space-x-4 overflow-x-auto border-b border-gray-200">
            <button
              onClick={() => setActiveTab("profile")}
              className={`py-3 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === "profile"
                  ? "border-[#7A56E8] text-[#7A56E8]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              프로필
            </button>
            <button
              onClick={() => setActiveTab("sales")}
              className={`py-3 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === "sales"
                  ? "border-[#7A56E8] text-[#7A56E8]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              판매내역
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8">
        {/* 프로필 탭 컨텐츠 */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            {/* 프로필 통계 */}
            <ProfileStats
              user={displayProfile}
              isOwnProfile={true}
              onAvatarUpdate={handleAvatarUpload}
            />

            {/* 자기소개 */}
            <ProfileAbout
              user={displayProfile}
              isOwnProfile={true}
              onUpdate={handleProfileUpdate}
              onEditNickname={() => setShowNicknameEdit(true)}
            />

            {/* 계정 설정 */}
            <Card>
              <h3 className="text-base font-semibold text-gray-900 mb-5">
                계정 설정
              </h3>
              <div className="space-y-3">
                {/* 이메일 */}
                <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                    <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">
                        이메일 인증
                      </p>
                      {displayProfile.emailVerified && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap flex-shrink-0">
                          인증됨
                        </span>
                      )}
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {displayProfile.email}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={displayProfile.emailVerified ? "outline" : "primary"}
                    onClick={() => setShowEmailInput(true)}
                    className={`text-xs sm:text-sm flex-shrink-0 ${
                      displayProfile.emailVerified
                        ? "border-[#6C4EF3] text-[#6C4EF3] hover:bg-[#6C4EF3]/5"
                        : "bg-[#6C4EF3] hover:bg-[#5A3DD9] text-white"
                    }`}
                  >
                    <span className="hidden sm:inline">
                      {displayProfile.emailVerified ? "재인증" : "인증하기"}
                    </span>
                    <span className="sm:hidden">
                      {displayProfile.emailVerified ? "재인증" : "인증"}
                    </span>
                  </Button>
                </div>

                {/* 핸드폰 인증 */}
                <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-900">
                        핸드폰 인증 (SMS)
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {displayProfile.phoneVerified
                          ? `인증완료 ${displayProfile.phoneNumber || ""}`
                          : "SMS 코드로 간편 인증"}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={displayProfile.phoneVerified ? "outline" : "primary"}
                    onClick={() => setShowPhoneInput(true)}
                    className={`text-xs sm:text-sm ${
                      displayProfile.phoneVerified
                        ? "border-[#6C4EF3] text-[#6C4EF3] hover:bg-[#6C4EF3]/5"
                        : "bg-[#6C4EF3] hover:bg-[#5A3DD9] text-white"
                    }`}
                  >
                    <span className="hidden sm:inline">
                      {displayProfile.phoneVerified ? "변경" : "인증하기"}
                    </span>
                    <span className="sm:hidden">
                      {displayProfile.phoneVerified ? "변경" : "인증"}
                    </span>
                  </Button>
                </div>

                {/* 비밀번호 변경 */}
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                      <p className="text-xs sm:text-sm font-medium text-gray-900">
                        비밀번호 변경
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPasswordChange(true)}
                      className="text-xs sm:text-sm"
                    >
                      변경
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* 차단된 사용자 관리 */}
            <Card>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <h3 className="text-base font-semibold text-gray-900">
                    차단된 사용자 관리
                  </h3>
                </div>
                <Button
                  onClick={() => setShowBlockedUsers(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <Shield className="w-4 h-4" />
                  <span>관리</span>
                </Button>
              </div>
            </Card>

            {/* 배송지 관리 */}
            <Card>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-[#7A56E8]" />
                  <h3 className="text-base font-semibold text-gray-900">
                    배송지 관리
                  </h3>
                </div>
                <Button
                  onClick={() => setShowShippingAddress(true)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                >
                  <MapPin className="w-4 h-4" />
                  <span>관리</span>
                </Button>
              </div>
            </Card>

            {/* 고객지원 및 정보 */}
            <Card>
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                고객지원 및 정보
              </h3>
              <div className="space-y-2">
                <Link
                  href="/help"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                >
                  <span className="text-sm text-gray-700">도움말</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  href="/contact"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                >
                  <span className="text-sm text-gray-700">문의하기</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  href="/terms"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                >
                  <span className="text-sm text-gray-700">이용약관</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
                <Link
                  href="/privacy"
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                >
                  <span className="text-sm text-gray-700">개인정보처리방침</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </Link>
              </div>
            </Card>

            {/* 로그아웃 */}
            <Card>
              <button
                onClick={async () => {
                  if (confirm("로그아웃 하시겠습니까?")) {
                    try {
                      await logout();
                      router.push("/");
                    } catch (error) {
                      console.error("로그아웃 실패:", error);
                      toast.error("로그아웃 중 오류가 발생했습니다.");
                      router.push("/");
                    }
                  }
                }}
                className="w-full flex items-center justify-center h-12 text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 rounded-xl transition-colors touch-manipulation"
              >
                <Lock className="w-4 h-4 mr-2" />
                로그아웃
              </button>
            </Card>
          </div>
        )}

        {/* 판매내역 탭 컨텐츠 */}
        {activeTab === "sales" && (
          <div className="space-y-6">
            {/* 판매중/거래중/거래완료 서브 탭 */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
              <div className="flex space-x-1">
                <button
                  onClick={() => setSalesTab("selling")}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    salesTab === "selling"
                      ? "text-[#7A56E8] border-b-2 border-[#7A56E8]"
                      : "text-gray-500"
                  }`}
                >
                  판매중 ({myItems.filter((item: any) => item.status === "active").length})
                </button>
                <button
                  onClick={() => setSalesTab("trading")}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    salesTab === "trading"
                      ? "text-[#7A56E8] border-b-2 border-[#7A56E8]"
                      : "text-gray-500"
                  }`}
                >
                  거래중 ({myItems.filter((item: any) => 
                    item.status === "reserved" || 
                    item.status === "escrow_completed" || 
                    item.status === "shipping" || 
                    item.status === "shipped"
                  ).length})
                </button>
                <button
                  onClick={() => setSalesTab("sold")}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    salesTab === "sold"
                      ? "text-[#7A56E8] border-b-2 border-[#7A56E8]"
                      : "text-gray-500"
                  }`}
                >
                  거래완료 ({myItems.filter((item: any) => item.status === "sold").length})
                </button>
              </div>
            </div>

            {/* 판매중 상품 목록 */}
            {salesTab === "selling" && (
              <div className="space-y-4">
                {itemsLoading ? (
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-gray-100 rounded-2xl aspect-square animate-pulse" />
                    ))}
                  </div>
                ) : myItems.filter((item: any) => item.status === "active").length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {myItems
                      .filter((item: any) => item.status === "active")
                      .map((item: any) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          onClick={() => router.push(`/product/${item.id}`)}
                          currentUserId={currentUser?.uid}
                        />
                      ))}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      판매중인 상품이 없습니다
                    </h3>
                    <p className="text-gray-600 mb-4">
                      새로운 상품을 등록해보세요.
                    </p>
                    <Button
                      onClick={() => router.push("/product/new")}
                      className="bg-[#7A56E8] hover:bg-[#6A46D8] text-white"
                    >
                      상품 등록하기
                    </Button>
                  </Card>
                )}
              </div>
            )}

            {/* 거래중 상품 목록 */}
            {salesTab === "trading" && (
              <div className="space-y-4">
                {itemsLoading ? (
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-gray-100 rounded-2xl aspect-square animate-pulse" />
                    ))}
                  </div>
                ) : myItems.filter((item: any) => 
                  item.status === "reserved" || 
                  item.status === "escrow_completed" || 
                  item.status === "shipping" || 
                  item.status === "shipped"
                ).length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {myItems
                      .filter((item: any) => 
                        item.status === "reserved" || 
                        item.status === "escrow_completed" || 
                        item.status === "shipping" || 
                        item.status === "shipped"
                      )
                      .map((item: any) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          onClick={() => router.push(`/product/${item.id}`)}
                          currentUserId={currentUser?.uid}
                        />
                      ))}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      거래중인 상품이 없습니다
                    </h3>
                    <p className="text-gray-600">
                      현재 진행 중인 거래가 없습니다.
                    </p>
                  </Card>
                )}
              </div>
            )}

            {/* 거래완료 상품 목록 */}
            {salesTab === "sold" && (
              <div className="space-y-4">
                {itemsLoading ? (
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-gray-100 rounded-2xl aspect-square animate-pulse" />
                    ))}
                  </div>
                ) : myItems.filter((item: any) => item.status === "sold").length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {myItems
                      .filter((item: any) => item.status === "sold")
                      .map((item: any) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          onClick={() => router.push(`/product/${item.id}`)}
                          currentUserId={currentUser?.uid}
                        />
                      ))}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      거래완료된 상품이 없습니다
                    </h3>
                    <p className="text-gray-600">
                      아직 완료된 거래가 없습니다.
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 닉네임 수정 모달 */}
      {currentUser && (
        <EditNicknameModal
          isOpen={showNicknameEdit}
          onClose={() => setShowNicknameEdit(false)}
          currentNickname={displayProfile?.nickname || currentUser.nickname || ""}
          userId={currentUser.uid}
          onUpdate={handleNicknameUpdate}
        />
      )}

      {/* 차단된 사용자 모달 */}
      <BlockedUsersModal
        isOpen={showBlockedUsers}
        onClose={() => setShowBlockedUsers(false)}
        onUnblock={(blockedUid) => {
          console.log("사용자 차단 해제됨:", blockedUid);
        }}
      />

      {/* 배송지 관리 모달 */}
      {currentUser && (
        <ShippingAddressModal
          isOpen={showShippingAddress}
          onClose={() => setShowShippingAddress(false)}
          userId={currentUser.uid}
        />
      )}

      {/* 이메일 입력 모달 */}
      {currentUser && (
        <EmailInputModal
          isOpen={showEmailInput}
          onClose={() => setShowEmailInput(false)}
          currentEmail={currentUser.email || ""}
          onSuccess={() => {
            refreshUser();
          }}
        />
      )}

      {/* 핸드폰 입력 모달 */}
      <PhoneInputModal
        isOpen={showPhoneInput}
        onClose={() => setShowPhoneInput(false)}
        currentPhone={displayProfile?.phoneNumber || ""}
        onSuccess={() => {
          refreshUser();
        }}
      />

      {/* 비밀번호 변경 모달 */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">비밀번호 변경</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  현재 비밀번호
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="현재 비밀번호를 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="새 비밀번호 (10자 이상, 소문자+숫자+특수문자)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    confirmPassword && newPassword
                      ? confirmPassword === newPassword
                        ? "border-green-500 focus:ring-green-500"
                        : "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                  placeholder="새 비밀번호를 다시 입력하세요"
                />
                {confirmPassword && newPassword && (
                  <p
                    className={`mt-1 text-sm ${
                      confirmPassword === newPassword
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {confirmPassword === newPassword
                      ? "✓ 비밀번호가 일치합니다"
                      : "✗ 비밀번호가 일치하지 않습니다"}
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  className="flex-1"
                >
                  취소
                </Button>
                <Button
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                  className="flex-1"
                >
                  {changingPassword ? "변경 중..." : "변경"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 거래지역 변경 모달 */}
      {showRegionEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">거래지역 선택</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  시/도
                </label>
                <select
                  value={selectedProvince}
                  onChange={e => {
                    setSelectedProvince(e.target.value);
                    setSelectedCity("");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">시/도를 선택하세요</option>
                  {KOREAN_REGIONS_DETAILED.map(region => (
                    <option key={region.value} value={region.value}>
                      {region.label}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProvince && 
                KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)?.cities && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    시/군/구
                  </label>
                  <select
                    value={selectedCity}
                    onChange={e => setSelectedCity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">시/군/구를 선택하세요 (선택사항)</option>
                    {KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)?.cities?.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    시/군/구는 선택사항입니다. 시/도만 선택해도 됩니다.
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRegionEdit(false);
                    setSelectedProvince("");
                    setSelectedCity("");
                  }}
                  className="flex-1"
                >
                  취소
                </Button>
                <Button 
                  onClick={handleRegionChange} 
                  disabled={!selectedProvince}
                  className="flex-1"
                >
                  저장
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    }>
      <MyPageContent />
    </Suspense>
  );
}
